import { generatePath } from 'react-router-dom';

import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import type { LinkTo } from '@/components/ui/link';
import type { Alert } from '@/entities/alert';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';

export type ActionDataProps = {
  alert?: Alert;
};

export type ActionData = {
  to: LinkTo | undefined;
  isLoading: boolean;
};

export function useActionData({
  alert,
}: ActionDataProps): ActionData | undefined {
  const me = useMe();
  const featureFlag = useFeatureFlag();
  const getAllSites = GetAllSites.useQuery({
    enabled: me.config.type !== 'mobility',
  });

  if (!alert) {
    return undefined;
  }

  const defaultSearchParams = new URLSearchParams({
    [`${alert.level}_id`]: `"${alert.fullFrontendId}"`,
    device: alert.id,
  }).toString();

  if (me.config.type === 'mobility' && featureFlag('details')) {
    return {
      to: {
        pathname: generatePath('/vehicle/:id/details', {
          id: alert.systemFrontendId ?? '',
        }),
        search: defaultSearchParams,
      },
      isLoading: false,
    };
  }

  if (getAllSites.isLoading) {
    return { to: undefined, isLoading: true };
  }

  const relevantAsset = getAllSites.data?.find(
    (asset) => alert.systemFrontendId === asset.fullFrontendId,
  );

  if (!relevantAsset) {
    return undefined;
  }

  if (alert.level === 'pcs') {
    return {
      to: {
        pathname: generatePath('/asset/:siteId/pcs', {
          siteId: relevantAsset.id,
        }),
        search: new URLSearchParams({
          [`site`]: alert.systemFrontendId ?? '',
          [`pcs`]: alert.fullFrontendId ?? '',
        }).toString(),
        hash: alert.type,
      },
      isLoading: false,
    };
  }

  if (featureFlag('details')) {
    return {
      to: {
        pathname: generatePath('/asset/:siteId/details', {
          siteId: relevantAsset.id,
        }),
        search: defaultSearchParams,
      },
      isLoading: false,
    };
  }

  return undefined;
}
